import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useCatalogAdmin, useCatalogMutation } from "../../hooks/catalog";
import { api } from "../../lib/api";
import { Empty } from "../ui/Badges";
import { CombosPanel } from "./CombosPanel";
import { ProductsPanel } from "./ProductsPanel";
import { RulesPanel } from "./RulesPanel";
import { StockPanel } from "./StockPanel";

type Sub = "pricing" | "products" | "combos" | "stock";
const TABS: Sub[] = ["pricing", "products", "combos", "stock"];

/** The default billing mode of the branch: post-paid (bill at the exit scan) or pre-paid (fixed time paid now). */
function BillingMode({ mode, canEdit }: { mode: "prepaid" | "postpaid"; canEdit: boolean }) {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const save = useCatalogMutation((m: "prepaid" | "postpaid") => api.put("/catalog-admin/settings", { defaultBillingMode: m }));
  async function pick(m: "prepaid" | "postpaid") {
    if (m === mode) return;
    try {
      await save.mutateAsync(m);
      toast(t("pricing.saved"), "success");
    } catch (e) {
      toastError(e);
    }
  }
  return (
    <div className="card space-y-2 p-4 sm:p-5">
      <div className="text-xs font-black text-cosmic-navy">🧾 {t("pricing.mode.title")}</div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" role="radiogroup" aria-label={t("pricing.mode.title")}>
        {(["postpaid", "prepaid"] as const).map((m) => (
          <button key={m} type="button" role="radio" data-mode={m} aria-checked={mode === m} disabled={!canEdit || save.isPending} onClick={() => void pick(m)}
            className={`rounded-2xl border-2 p-3 text-start transition ${mode === m ? "border-cosmic-purple bg-purple-50" : "border-gray-200 bg-white hover:border-cosmic-purple"} ${canEdit ? "" : "cursor-default"}`}>
            <div className={`text-xs font-black ${mode === m ? "text-cosmic-purple" : "text-gray-700"}`}>{m === "postpaid" ? "🧾" : "⏳"} {t(`pricing.mode.${m}`)}{mode === m && ` ✓ ${t("pricing.mode.default")}`}</div>
            <div className="mt-0.5 text-[11px] font-semibold text-gray-500">{t(`pricing.mode.${m}Help`)}</div>
          </button>
        ))}
      </div>
      <p className="text-[11px] font-semibold text-gray-500">{t("pricing.mode.note")}</p>
    </div>
  );
}

/**
 * Catalog & Pricing: everything the administrator edits to run the park. Managers can look, administrators and owners edit.
 * Prices come only from here (price rules + products); nothing is hardcoded in the cashier screens.
 */
export function CatalogPanel() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const isAdmin = can("pricing_edit");
  const { data, isLoading, isError } = useCatalogAdmin();
  const [sub, setSub] = useState<Sub>("pricing");
  return (
    <div className="space-y-5">
      <div className="card flex items-center gap-3.5 p-5">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-cosmic-purple to-solar-orange text-2xl shadow-cosmic" aria-hidden>🏷️</div>
        <div>
          <h1 className="text-xl font-black tracking-tight">{t("pricing.title")}</h1>
          <p className="text-xs font-medium text-gray-500">{isAdmin ? t("pricing.subtitle") : t("pricing.readOnly")}</p>
        </div>
      </div>
      {isLoading ? (
        <Empty>{t("common.loading")}</Empty>
      ) : isError || !data ? (
        <Empty>{t("pricing.loadError")}</Empty>
      ) : (
        <>
          <BillingMode mode={data.settings.defaultBillingMode} canEdit={isAdmin} />
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label={t("pricing.title")}>
            {TABS.map((x) => (
              <button key={x} role="tab" type="button" data-tab={x} aria-selected={sub === x} onClick={() => setSub(x)} className={`rounded-xl px-4 py-2 text-xs font-black transition ${sub === x ? "bg-cosmic-purple text-white shadow-cosmic" : "bg-white text-gray-600 hover:bg-purple-50"}`}>{t(`pricing.tabs.${x}`)}</button>
            ))}
          </div>
          {sub === "pricing" && <RulesPanel data={data} canEdit={isAdmin} />}
          {sub === "products" && <ProductsPanel data={data} canEdit={isAdmin} />}
          {sub === "combos" && <CombosPanel data={data} canEdit={isAdmin} />}
          {sub === "stock" && <StockPanel data={data} canEdit={isAdmin} canReceive />}
        </>
      )}
    </div>
  );
}
